/**
 * FormInboxResponseModal.tsx
 *
 * Detail modal for a row in the Forms Inbox (`/surveys/all`).
 *
 * Wraps the legacy-faithful SurveyResponseBody renderer (already used in
 * the patient-detail Forms tab) and adds the action bar from
 * `patient_details_content_provider_authnet.php` (Complete / Delete /
 * Send to Notes). Auto-fires mark-read on open, mirroring the legacy
 * behavior of marking a row read the moment its detail page renders.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, ExternalLink } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { SurveyResponseBody } from '@/pages/patients/SurveyResponseModal';
import { completedFormPdfFileName } from '@/services/completedFormPdf';
import { useCompletedFormPdf } from '@/hooks/useCompletedFormPdf';
import {
  getFormsInboxResponseDetail,
  getFormsInboxResponsePdf,
  markFormsInboxResponseRead,
  markFormsInboxResponseUnread,
  sendFormsInboxResponseToNotes,
  trashFormsInboxResponse,
  type FormsInboxResponseDetail,
  type FormsInboxStatus,
} from '@/services/formsInbox.service';

interface FormInboxResponseModalProps {
  responseId: number | null;
  open: boolean;
  onClose: () => void;
  /** Which tab the modal was opened from — controls "Mark as Read" vs "Mark as Unread". */
  status?: FormsInboxStatus;
}

type ToastKind = 'success' | 'error';

interface InlineToast {
  kind: ToastKind;
  message: string;
}

function OwnerLink({ label, to, name }: { label: string; to: string; name: string }) {
  return (
    <p className="mt-0.5 text-sm text-slate-600">
      {label}:{' '}
      <Link
        to={to}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 font-medium text-blue-700 underline-offset-2 hover:underline"
      >
        {name}
        <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
      </Link>
    </p>
  );
}

/**
 * Who the form is attached to, under the title (SCRUM-2419): the patient, or
 * for a form with no patient the lead it came in as. The name opens their
 * page in a new tab, so the inbox stays where it was.
 */
function FormOwnerLine({ detail }: { detail: FormsInboxResponseDetail }) {
  const patientId = detail.response.patientId;
  if (patientId > 0) {
    const name = detail.patient ? `${detail.patient.firstName} ${detail.patient.lastName}`.trim() : '';
    return <OwnerLink label="Patient" to={`/patients/${patientId}`} name={name !== '' ? name : `Patient #${patientId}`} />;
  }
  const lead = detail.lead;
  if (lead) {
    return <OwnerLink label="Lead" to={`/leads/${lead.id}`} name={lead.name !== '' ? lead.name : `Lead #${lead.id}`} />;
  }
  return <p className="mt-0.5 text-sm text-slate-500">Not attached to a patient or lead</p>;
}

export function FormInboxResponseModal({ responseId, open, onClose, status = 'unread' }: FormInboxResponseModalProps) {
  const queryClient = useQueryClient();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [toast, setToast] = useState<InlineToast | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['forms-inbox-response', responseId],
    queryFn: () => getFormsInboxResponseDetail(responseId!),
    enabled: open && responseId !== null,
    // Load the answers fresh on every open: an unfinished form keeps
    // changing while the visitor fills it in. No cache, so no old answers
    // flash before the new ones arrive.
    staleTime: 0,
    gcTime: 0,
  });

  // Reset transient UI state every time the modal is reopened with a new id.
  useEffect(() => {
    if (open) {
      setConfirmingDelete(false);
      setToast(null);
    }
  }, [open, responseId]);

  const invalidateLists = () => {
    queryClient.invalidateQueries({ queryKey: ['forms-inbox'] });
    queryClient.invalidateQueries({ queryKey: ['forms-inbox-unread-count'] });
  };

  const isUnread = status === 'unread';
  // An unfinished visit has no read state to toggle — it stays on Incomplete
  // until the visitor submits.
  const canToggleRead = status !== 'incomplete';

  const toggleReadMutation = useMutation({
    mutationFn: () =>
      isUnread
        ? markFormsInboxResponseRead(responseId!)
        : markFormsInboxResponseUnread(responseId!),
    onSuccess: () => {
      invalidateLists();
      onClose();
    },
    onError: (err) => {
      setToast({
        kind: 'error',
        message: err instanceof Error ? err.message : `Failed to mark form as ${isUnread ? 'read' : 'unread'}.`,
      });
    },
  });

  const trashMutation = useMutation({
    mutationFn: () => trashFormsInboxResponse(responseId!),
    onSuccess: () => {
      invalidateLists();
      onClose();
    },
    onError: (err) => {
      setToast({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Failed to delete form.',
      });
    },
  });

  const sendToNotesMutation = useMutation({
    mutationFn: () => sendFormsInboxResponseToNotes(responseId!),
    onSuccess: (result) => {
      // Notes that we just wrote should appear in the patient's chart
      // immediately; nuke any cached patient-notes lists so they refetch.
      queryClient.invalidateQueries({ queryKey: ['patient-notes'] });
      setToast({
        kind: 'success',
        message: result.message ?? 'Survey content successfully saved as patient note.',
      });
    },
    onError: (err) => {
      setToast({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Failed to send to notes.',
      });
    },
  });

  // By response id alone, so a lead or unattached form (patientId 0) works too.
  const pdf = useCompletedFormPdf({
    fetchPdf: () => getFormsInboxResponsePdf(responseId!),
    setToast,
  });
  // Only a submitted form has a PDF: an Incomplete-tab visit is refused by
  // the server, so neither button is offered for it.
  const canGetPdf = status !== 'incomplete' && (data?.response.dateCompleted ?? '') !== '';

  const handleDownloadPdf = () => {
    if (!data) return;
    pdf.download(completedFormPdfFileName(data.survey.title, data.response.dateCompleted ?? data.response.dateCreated));
  };

  const patientId = data?.response.patientId ?? 0;
  const canSendToNotes = patientId > 0;

  const handleClose = () => {
    if (toggleReadMutation.isPending || trashMutation.isPending || sendToNotesMutation.isPending) {
      return;
    }
    onClose();
  };

  const footerContent = data ? (
    <div className="flex flex-wrap items-center gap-2 w-full justify-end">
      {toast && (
        <span
          className={`mr-auto text-xs ${
            toast.kind === 'success' ? 'text-emerald-700' : 'text-red-600'
          }`}
        >
          {toast.message}
        </span>
      )}
      {confirmingDelete ? (
        <>
          <span className="text-sm text-slate-700">
            Permanently remove this submission from the inbox?
          </span>
          <Button
            variant="secondary"
            onClick={() => setConfirmingDelete(false)}
            disabled={trashMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => trashMutation.mutate()}
            disabled={trashMutation.isPending}
          >
            {trashMutation.isPending ? 'Deleting...' : 'Confirm Delete'}
          </Button>
        </>
      ) : (
        <>
          {canGetPdf && (
            <>
              <Button variant="secondary" onClick={handleDownloadPdf} disabled={pdf.pending}>
                {pdf.downloadPending ? 'Preparing…' : 'Download PDF'}
              </Button>
              <Button variant="secondary" onClick={pdf.print} disabled={pdf.pending}>
                {pdf.printPending ? 'Preparing…' : 'Print'}
              </Button>
            </>
          )}
          {canSendToNotes && (
            <Button
              variant="secondary"
              onClick={() => sendToNotesMutation.mutate()}
              disabled={sendToNotesMutation.isPending}
            >
              {sendToNotesMutation.isPending ? 'Sending...' : 'Send to Notes'}
            </Button>
          )}
          <Button
            variant="danger"
            onClick={() => setConfirmingDelete(true)}
          >
            Delete
          </Button>
          {canToggleRead && (
            <Button
              variant="primary"
              onClick={() => toggleReadMutation.mutate()}
              disabled={toggleReadMutation.isPending}
            >
              {toggleReadMutation.isPending
                ? (isUnread ? 'Marking Read...' : 'Marking Unread...')
                : (isUnread ? 'Mark as Read' : 'Mark as Unread')}
            </Button>
          )}
        </>
      )}
    </div>
  ) : undefined;

  return (
    <Modal isOpen={open} onClose={handleClose} title={
      <div>
        <h2 className="inline-flex items-center gap-2 text-base font-semibold text-slate-900">
          {data?.survey.title ?? 'Form'}
          {data?.response.wasRead === true && <span title="Read"><CheckCircle className="h-4 w-4 text-emerald-500" /></span>}
        </h2>
        {data && <FormOwnerLine detail={data} />}
      </div>
    } size="xxl" footer={footerContent}>
      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : isError || !data ? (
        <p className="py-6 text-center text-sm text-red-600">Failed to load response.</p>
      ) : (
        <SurveyResponseBody detail={data} responseId={responseId ?? 0} />
      )}
    </Modal>
  );
}

export default FormInboxResponseModal;
